import SearchIcon from '@mui/icons-material/Search';
import {
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';

interface TenderTableFiltersProps {
  filterOwner: 'all' | 'mine';
  onFilterOwnerChange: (
    event: React.MouseEvent<HTMLElement>,
    newFilter: 'all' | 'mine' | null
  ) => void;
  onFilterOwnerSelectChange: (event: SelectChangeEvent) => void;
  allLabel: string;
  searchTerm: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sortConfig: { key: string; direction: string };
  onSortChange: (event: SelectChangeEvent) => void;
  stateFilter: string[];
  onStateFilterChange: (event: SelectChangeEvent<string[]>) => void;
  teamsMap?: Record<string, string> | undefined;
  relevantTeamIds: Set<string>;
  selectedTeam: string;
  onTeamChange: (event: SelectChangeEvent) => void;
  usersMap?: Record<string, { displayName: string; teamId?: string }> | undefined;
  relevantUserIds: Set<string>;
  selectedUser: string;
  onUserChange: (event: SelectChangeEvent) => void;
}

const TenderTableFilters: React.FC<TenderTableFiltersProps> = ({
  filterOwner,
  onFilterOwnerChange,
  onFilterOwnerSelectChange,
  allLabel,
  searchTerm,
  onSearchChange,
  sortConfig,
  onSortChange,
  stateFilter,
  onStateFilterChange,
  teamsMap,
  relevantTeamIds,
  selectedTeam,
  onTeamChange,
  usersMap,
  relevantUserIds,
  selectedUser,
  onUserChange,
}) => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

  return (
    <Grid container spacing={2} sx={{ mt: 2 }} alignItems="center">
      {/* Row 1: View Toggle, Search, Sort */}
      <Grid size={{ xs: 12, md: 3 }}>
        {isLgUp ? (
          <ToggleButtonGroup
            value={filterOwner}
            exclusive
            onChange={onFilterOwnerChange}
            aria-label="Filter Tenders"
            size="small"
            fullWidth
            sx={{
              height: '35px',
              backgroundColor: theme.palette.greyLight,
              border: `1px solid ${theme.palette.text.primary}`,
              borderRadius: 3,
              p: 0.5,
              gap: 0.5,
              '& .MuiToggleButtonGroup-grouped': {
                border: 'none',
                borderRadius: 2,
                margin: 0,
                flex: 1,
              },
            }}
          >
            <ToggleButton
              value="mine"
              aria-label="My Tenders"
              sx={{
                fontSize: '0.875rem',
                color: theme.palette.text.primary,
                py: 0,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  border: `1px solid ${theme.palette.greyContrast}`,
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: theme.palette.background.paper,
                  },
                },
              }}
            >
              My Tenders
            </ToggleButton>
            <ToggleButton
              value="all"
              aria-label={allLabel}
              sx={{
                fontSize: '0.875rem',
                color: theme.palette.text.primary,
                py: 0,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  border: `1px solid ${theme.palette.greyContrast}`,
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: theme.palette.background.paper,
                  },
                },
              }}
            >
              {allLabel}
            </ToggleButton>
          </ToggleButtonGroup>
        ) : (
          <FormControl variant="outlined" size="small" sx={{ borderRadius: 3 }} fullWidth>
            <InputLabel id="filter-owner-label">View</InputLabel>
            <Select
              labelId="filter-owner-label"
              id="filter-owner-select"
              value={filterOwner}
              onChange={onFilterOwnerSelectChange}
              label="View"
              sx={{
                backgroundColor: theme.palette.greyLight,
                borderRadius: 3,
                '& .MuiSelect-select': { color: theme.palette.text.primary },
                '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                fieldset: { borderColor: theme.palette.text.primary, borderRadius: 3 },
              }}
            >
              <MenuItem value="mine">My Tenders</MenuItem>
              <MenuItem value="all">{allLabel}</MenuItem>
            </Select>
          </FormControl>
        )}
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          label="Search tenders (title, issuer, keyword, ID)"
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={onSearchChange}
          sx={{
            '& .MuiInputLabel-root': {
              color: theme.palette.text.primary,
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: theme.palette.primary.dark,
            },
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.palette.greyLight,
              borderRadius: 3,
              '& fieldset': {
                borderColor: theme.palette.text.primary,
                borderRadius: 3,
              },
              '&:hover fieldset': {
                borderColor: theme.palette.common.black,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.bidlogiq?.orange || theme.palette.primary.main,
              },
              '& input': {
                color: theme.palette.primary.main,
              },
              '&.Mui-focused input': {
                color: theme.palette.primary.main,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <SearchIcon
                sx={{ mr: 1, color: theme.palette.bidlogiq?.orange || theme.palette.primary.main }}
              />
            ),
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <FormControl variant="outlined" size="small" sx={{ borderRadius: 3 }} fullWidth>
          <InputLabel id="sort-by-label">Sort By</InputLabel>
          <Select
            labelId="sort-by-label"
            id="sort-by-select"
            value={`${sortConfig.key}-${sortConfig.direction}`}
            onChange={onSortChange}
            label="Sort By"
            sx={{
              backgroundColor: theme.palette.greyLight,
              borderRadius: 3,
              '& .MuiSelect-select': { color: theme.palette.text.primary },
              '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
              fieldset: { borderColor: theme.palette.text.primary, borderRadius: 3 },
            }}
          >
            <MenuItem value="submissionDeadline-desc">Deadline (Furthest out)</MenuItem>
            <MenuItem value="submissionDeadline-asc">Deadline (Closing soonest)</MenuItem>
            <MenuItem value="title-asc">Title (A-Z)</MenuItem>
            <MenuItem value="title-desc">Title (Z-A)</MenuItem>
            <MenuItem value="client-asc">Issuer (A-Z)</MenuItem>
            <MenuItem value="client-desc">Issuer (Z-A)</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {/* Filter Row: Locale, Team Filter, User Filter */}
      <Grid size={{ xs: 12 }} container spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
        {/* Col 1: Locale - Always visible */}
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl variant="outlined" size="small" sx={{ borderRadius: 3 }} fullWidth>
            <InputLabel id="region-filter-label">Filter by Region</InputLabel>
            <Select
              labelId="region-filter-label"
              id="region-filter-select"
              multiple
              value={stateFilter}
              onChange={onStateFilterChange}
              label="Filter by Region"
              renderValue={(selected) =>
                selected.length === 0 ? 'All Regions' : (selected as string[]).join(', ')
              }
              sx={{
                backgroundColor: theme.palette.greyLight,
                borderRadius: 3,
                '& .MuiSelect-select': { color: theme.palette.text.primary },
                '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                fieldset: { borderColor: theme.palette.text.primary, borderRadius: 3 },
              }}
            >
              {['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'].map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox checked={stateFilter.includes(name)} size="small" />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Col 2: Filter by Team */}
        {teamsMap && Object.keys(teamsMap).length > 0 && (
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl
              variant="outlined"
              size="small"
              sx={{ borderRadius: 3 }}
              fullWidth
              disabled={filterOwner === 'mine'}
            >
              <InputLabel id="team-filter-label">Filter by Team</InputLabel>
              <Select
                labelId="team-filter-label"
                id="team-filter-select"
                value={selectedTeam}
                onChange={onTeamChange}
                label="Filter by Team"
                sx={{
                  backgroundColor: theme.palette.greyLight,
                  borderRadius: 3,
                  '& .MuiSelect-select': { color: theme.palette.text.primary },
                  '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                  fieldset: { borderColor: theme.palette.text.primary, borderRadius: 3 },
                }}
              >
                <MenuItem value="all">All Teams</MenuItem>
                {Object.entries(teamsMap)
                  .filter(([id]) => relevantTeamIds.has(id))
                  .map(([id, name]) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Col 3: Filter by User */}
        {usersMap && Object.keys(usersMap).length > 0 && (
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl
              variant="outlined"
              size="small"
              sx={{ borderRadius: 3 }}
              fullWidth
              disabled={filterOwner === 'mine'}
            >
              <InputLabel id="user-filter-label">Filter by User</InputLabel>
              <Select
                labelId="user-filter-label"
                id="user-filter-select"
                value={selectedUser}
                onChange={onUserChange}
                label="Filter by User"
                sx={{
                  backgroundColor: theme.palette.greyLight,
                  borderRadius: 3,
                  '& .MuiSelect-select': { color: theme.palette.text.primary },
                  '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                  fieldset: { borderColor: theme.palette.text.primary, borderRadius: 3 },
                }}
              >
                <MenuItem value="all">All Users</MenuItem>
                {Object.entries(usersMap)
                  .filter(
                    ([id, user]) =>
                      (selectedTeam === 'all' || !user.teamId || user.teamId === selectedTeam) &&
                      relevantUserIds.has(id)
                  )
                  .map(([id, user]) => (
                    <MenuItem key={id} value={id}>
                      {user.displayName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default TenderTableFilters;
